import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, ArrowRight, ArrowLeft, Shield, Mail, Check } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';

export default function CommunitySetupPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const functions = getFunctions();

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Gate: only super admins can access this page
  if (!user?.isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 max-w-md text-center">
          <Shield className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-500 mb-4">Only platform administrators can create communities.</p>
          <button
            onClick={() => navigate('/')}
            className="text-quiltt-purple hover:underline"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const handleCreate = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Community name is required');
      return;
    }

    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      const createCommunity = httpsCallable(functions, 'createCommunity');
      const result = await createCommunity({
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
        adminEmail: adminEmail.trim() || null,
        adminName: adminName.trim() || null
      });

      if (result.data.success) {
        if (result.data.inviteSent) {
          // Admin invite was sent — show success and stay on page
          setSuccess({
            communityName: name.trim(),
            communityId: result.data.communityId,
            adminEmail: adminEmail.trim()
          });
          // Reset form for next community
          setName('');
          setAddress('');
          setPhone('');
          setAdminEmail('');
          setAdminName('');
        } else {
          // No admin email — super admin was added, redirect to dashboard
          window.location.href = `/community/${result.data.communityId}`;
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to create community');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-sm p-8 max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-quiltt-purple rounded-xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Create a Community</h1>
          <p className="text-gray-500">
            Set up a new community and assign their admin.
          </p>
        </div>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800">{success.communityName} created!</p>
                <p className="text-sm text-green-700 mt-1">
                  Admin invite sent to {success.adminEmail}. They'll receive an email with a link to join and manage the community.
                </p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {/* Community Info */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Community Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Sunrise Senior Living"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="123 Main St, City, State"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
            />
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 pt-4 mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-700">Community Admin (optional)</p>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              If provided, this person will receive an invite email to manage the community. 
              Leave blank to manage it yourself.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                <input
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  placeholder="Jane Smith"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Email</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="jane@community.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-quiltt-500 focus:border-transparent outline-none"
                />
              </div>
            </div>
          </div>

          <button
            onClick={handleCreate}
            disabled={creating || !name.trim()}
            className="w-full flex items-center justify-center gap-2 bg-quiltt-purple hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors mt-6"
          >
            {creating ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Create Community
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 py-2 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>
        </div>
      </div>
    </div>
  );
}
